import { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import stripePromise from '../../config/stripe';
import toast from 'react-hot-toast';

/**
 * Inner form that renders inside the Stripe <Elements> provider.
 * Uses the PaymentElement which shows Card / PayPal / Google Pay / Apple Pay tabs.
 */
const PaymentForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setErrorMessage(null);

    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/dashboard/settings?tab=billing&setup=success`,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message);
      setSubmitting(false);
    } else {
      // Setup succeeded without redirect (card/wallet payments)
      toast.success('Payment method added successfully!');
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{
          layout: 'tabs',
        }}
      />

      {errorMessage && (
        <p className="text-sm text-red-600 dark:text-red-400">{errorMessage}</p>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={!stripe || submitting}
          className="btn btn-primary btn-sm flex items-center gap-2"
        >
          {submitting && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
          {submitting ? 'Saving...' : 'Save Payment Method'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="btn btn-secondary btn-sm"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

/**
 * Wrapper that initializes Stripe Elements with a SetupIntent clientSecret
 * and renders the PaymentForm inside.
 */
const AddPaymentMethodForm = ({ clientSecret, onSuccess, onCancel }) => {
  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#4f46e5',
      borderRadius: '8px',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
  };

  return (
    <div className="mt-4 p-4 border border-gray-200 dark:border-dark-700 rounded-lg bg-white dark:bg-dark-900">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Add Payment Method</h4>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
        <PaymentForm onSuccess={onSuccess} onCancel={onCancel} />
      </Elements>
    </div>
  );
};

export default AddPaymentMethodForm;
